import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export const FollowersDialog = ({ userId, open, onOpenChange }) => {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/users/${userId}/followers`)
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const toggleFollow = async (u) => {
    setBusy(u.id);
    try {
      if (u.is_following) {
        await api.delete(`/follow/user/${u.id}`);
      } else {
        await api.post('/follow', { target_type: 'user', target_id: u.id });
      }
      setList((prev) => prev.map((p) => (p.id === u.id ? { ...p, is_following: !u.is_following } : p)));
      toast.success(u.is_following ? `Unfollowed ${u.display_name}` : `Following ${u.display_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update follow');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#101015] sm:max-w-md" data-testid="followers-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Followers</DialogTitle>
          <DialogDescription className="text-white/60">People following this profile</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-white/60">
              <Loader2 className="h-4 w-4 th-spin" /> Loading…
            </div>
          )}
          {!loading && list.length === 0 && (
            <p className="py-8 text-center text-sm text-white/60" data-testid="followers-empty">No followers yet.</p>
          )}
          {!loading && list.map((u) => (
            <div key={u.id} className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-white/5" data-testid={`follower-row-${u.id}`}>
              <button
                onClick={() => { onOpenChange(false); nav(`/user/${u.id}`); }}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                data-testid={`follower-link-${u.id}`}
              >
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt={u.display_name} className="h-9 w-9 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F6B24A]/15 text-sm font-bold text-[#F6B24A]">
                    {(u.display_name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-white">{u.display_name}</span>
                  <span className="block text-xs capitalize text-white/50">{u.role === 'venue_owner' ? 'Venue owner' : u.role || 'member'}</span>
                </span>
              </button>
              {me?.id !== u.id && (
                <Button
                  onClick={() => toggleFollow(u)}
                  disabled={busy === u.id}
                  variant="outline"
                  className={`h-8 shrink-0 rounded-full px-4 text-xs font-semibold ${
                    u.is_following
                      ? 'border-white/15 bg-white/5 text-white/70 hover:bg-white/10'
                      : 'border-[#F6B24A]/50 bg-[#F6B24A]/15 text-[#F6B24A] hover:bg-[#F6B24A]/25'
                  }`}
                  data-testid={`follower-follow-${u.id}`}
                >
                  {busy === u.id ? <Loader2 className="h-3.5 w-3.5 th-spin" /> : u.is_following ? 'Following' : 'Follow back'}
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FollowersDialog;
